import { useCallback, useState } from "react";
import { toast } from "sonner";
import { PackagePlus, SearchX } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArticleDialog } from "@/components/ArticleDialog";
import { QuickMovementDialog } from "@/components/QuickMovementDialog";
import { useKeyboardScanner } from "@/hooks/useKeyboardScanner";
import { apiError, lookupCode } from "@/lib/api";
import { scanFeedback } from "@/lib/feedback";

function UnknownCodeDialog({ code, onClose, onCreate }) {
  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="unknown-code-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl uppercase"><SearchX className="h-6 w-6 text-rose-600" /> Code inconnu</DialogTitle>
          <DialogDescription>Aucun article ni kit ne correspond à <span className="font-mono font-semibold text-slate-900" data-testid="unknown-code-value">{code}</span>.</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-12" onClick={onClose} data-testid="unknown-code-close">Fermer</Button>
          <Button className="h-12 gap-2" onClick={onCreate} data-testid="unknown-code-create-button"><PackagePlus className="h-4 w-4" /> Créer un article avec ce code-barres</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useScanHub({ listen = true, enabled = true, onChanged } = {}) {
  const [target, setTarget] = useState(null);
  const [unknown, setUnknown] = useState(null);
  const [create, setCreate] = useState(null);

  const showUnknown = useCallback((code) => { setTarget(null); setUnknown(code); }, []);
  const handle = useCallback(async (code) => {
    try {
      const data = await lookupCode(code);
      if (data.kind === "none") {
        scanFeedback(false, `Code inconnu : ${code}`);
        showUnknown(code);
        return;
      }
      scanFeedback(true, `${data.item.code} — ${data.item.designation}`);
      setUnknown(null);
      setTarget(data);
    } catch (e) {
      scanFeedback(false, "Erreur de recherche");
      toast.error(apiError(e));
    }
  }, [showUnknown]);

  useKeyboardScanner(handle, listen && enabled && !create);

  const ui = (
    <>
      <QuickMovementDialog target={target} onClose={() => setTarget(null)} onDone={onChanged} />
      <UnknownCodeDialog code={unknown} onClose={() => setUnknown(null)} onCreate={() => { setCreate(unknown); setUnknown(null); }} />
      <ArticleDialog open={!!create} article={null} initial={{ barcode: create || "" }}
        onOpenChange={(o) => !o && setCreate(null)}
        onSaved={(a) => { onChanged?.(); if (a) setTarget({ kind: "article", item: a }); }} />
    </>
  );
  return { handle, showUnknown, ui };
}

export function ScanHub(props) {
  return useScanHub(props).ui;
}
